import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { OrderDetailsPageContext } from "@insite/content-library/Pages/OrderDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { css } from "styled-components";

export interface OrderDetailsShippingMethodStyles {
    titleText?: TypographyProps;
    description?: TypographyProps;
    wrapper?: InjectableCss;
    signatureWrapper: InjectableCss;
}

export const shippingMethodStyles: OrderDetailsShippingMethodStyles = {
    titleText: {
        variant: "h6",
        as: "h2",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 2px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            span {
                padding-left: 5px;
            }
        `,
    },
    signatureWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 5px;
            padding: 10px;
            padding-left: 0;
            background: #f8f9fa;
            span {
                padding-left: 5px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 110%;
                    `,
                    css`
                        width: 110%;
                    `,
                    css`
                        width: 130%;
                    `,
                    css`
                        width: 149%; 
                    `,
                    css`
                        width: 110%;
                    `,
                ])}
        `,
    },
};

const styles = shippingMethodStyles;

const OrderDetailsCustomerDetails: React.FunctionComponent = () => {
    const { value: order } = useContext(OrderStateContext);
    const INVOICED = "Invoiced";

    if (!order) {
        return null;
    }

    const hasShippingMethod =
        (order.fulfillmentMethod === FulfillmentMethod.Ship || !order.fulfillmentMethod) && order.shipCode;

    return (
        <>
            {order.statusDisplay && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText} id="orderDetailsStatus">
                        {translate("Status: ")}
                    </Typography>
                    <Typography {...styles.description} aria-labelledby="orderDetailsStatus">
                        {order.statusDisplay}
                    </Typography>
                </StyledWrapper>
            )}
            {order.orderDate && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText} id="orderDate">
                        {translate("Order Date: ")}
                    </Typography>
                    <Typography {...styles.description} aria-labelledby="orderDate">
                        <LocalizedDateTime
                            dateTime={order.orderDate}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </StyledWrapper>
            )}
            {order.requestedDeliveryDateDisplay && (
                <StyledWrapper {...styles.wrapper}>
                    {order.status === INVOICED && (
                        <>
                            <Typography {...styles.titleText}>{translate("Invoice Date: ")}</Typography>
                            <Typography {...styles.description} data-test-selector="orderDetails_requestedDeliveryDate">
                                <LocalizedDateTime
                                    dateTime={new Date(order.requestedDeliveryDateDisplay)}
                                    options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                />
                            </Typography>
                        </>
                    )}
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.titleText} id="OrderWriter">
                    {translate("Customer PO: ")}
                </Typography>
                <Typography {...styles.description} aria-labelledby="OrderWriter">
                    {order.customerPO}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.titleText} id="OrderWriter">
                    {translate("Order By: ")}
                </Typography>
                <Typography {...styles.description} aria-labelledby="OrderWriter">
                    {order?.properties.orderedBy}
                </Typography>
            </StyledWrapper>

            {order.terms && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText} id="OrderWriter">
                        {translate("Terms: ")}
                    </Typography>
                    <Typography {...styles.description} aria-labelledby="OrderWriter">
                        {order.terms}
                    </Typography>
                </StyledWrapper>
            )}
            {order.salesperson && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText} id="OrderWriter">
                        {translate("Salesperson: ")}
                    </Typography>
                    <Typography {...styles.description} aria-labelledby="OrderWriter">
                        {order.salesperson}
                    </Typography>
                </StyledWrapper>
            )}
            {order?.properties.writer && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText} id="OrderWriter">
                        {translate("Writer: ")}
                    </Typography>
                    <Typography {...styles.description} aria-labelledby="OrderWriter">
                        {order?.properties.writer}
                    </Typography>
                </StyledWrapper>
            )}
            {order?.properties.customerReleaseNumber && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText} id="OrderCustomerReleaseNumber">
                        {translate("Customer Release Number: ")}
                    </Typography>
                    <Typography {...styles.description} aria-labelledby="OrderCustomerReleaseNumber">
                        {order?.properties.customerReleaseNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {hasShippingMethod && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Shipping Method: ")}</Typography>
                    <Typography {...styles.description}>{order.shipViaDescription || order.shipCode}</Typography>
                </StyledWrapper>
            )}
            {order?.properties.signature && (
                <StyledWrapper {...styles.signatureWrapper}>
                    <Typography {...styles.titleText}>{translate("Delivery Confirmation Signature: ")}</Typography>
                    <Typography {...styles.description}>
                        <img src={order.properties?.signature} alt="" />
                    </Typography>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsCustomerDetails,
    definition: {
        allowedContexts: [OrderDetailsPageContext],
        group: "Order Details",
    },
};

export default widgetModule;
